import React, { useState, useEffect } from 'react';
import { PageTracker } from '../../../components/PageTracker.tsx';
import '../../../styles/dev-utils-theme.css';
import { 
  Wifi, 
  Globe, 
  MapPin, 
  Server, 
  Activity, 
  Download, 
  Upload, 
  Timer, 
  Zap,
  Search,
  Loader,
  AlertCircle,
  CheckCircle,
  Info,
  Monitor,
  Router,
  Shield,
  Eye,
  Copy,
  RefreshCw
} from 'lucide-react';

interface SpeedTestResult {
  download: number;
  upload: number;
  ping: number;
  jitter: number;
  timestamp: string;
}

interface IPInfo {
  ip: string;
  city: string;
  region: string;
  country: string;
  countryCode: string;
  timezone: string;
  isp: string;
  org: string;
  as: string;
  lat: number;
  lon: number;
}

interface WebsiteInfo {
  domain: string;
  ip: string;
  status: string;
  responseTime: number;
  headers: Record<string, string>;
}

export default function NetworkAnalyzer() {
  const [activeTab, setActiveTab] = useState<'speed' | 'ip' | 'website' | 'dns'>('speed');
  const [isTestingSpeed, setIsTestingSpeed] = useState(false);
  const [speedResult, setSpeedResult] = useState<SpeedTestResult | null>(null);
  const [myIP, setMyIP] = useState<IPInfo | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteInfo, setWebsiteInfo] = useState<WebsiteInfo | null>(null);
  const [isLoadingIP, setIsLoadingIP] = useState(false);
  const [isAnalyzingWebsite, setIsAnalyzingWebsite] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load my IP info on component mount
  useEffect(() => {
    loadMyIPInfo();
  }, []);

  const loadMyIPInfo = async () => {
    setIsLoadingIP(true);
    setError(null);
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      setMyIP({
        ip: data.ip,
        city: data.city,
        region: data.region,
        country: data.country_name,
        countryCode: data.country_code,
        timezone: data.timezone,
        isp: data.org,
        org: data.org,
        as: data.asn,
        lat: data.latitude,
        lon: data.longitude
      });
    } catch (err) {
      setError('Failed to load IP information');
    } finally {
      setIsLoadingIP(false);
    }
  };

  const runSpeedTest = async () => {
    setIsTestingSpeed(true);
    setError(null);
    
    try {
      // Simulate speed test (in a real app, you'd use a speed test API)
      const startTime = Date.now();
      
      // Simulate download test
      const downloadPromise = simulateSpeedTest('download');
      const uploadPromise = simulateSpeedTest('upload');
      const pingPromise = simulatePingTest();
      
      const [download, upload, ping] = await Promise.all([
        downloadPromise,
        uploadPromise,
        pingPromise
      ]);
      
      const result: SpeedTestResult = {
        download,
        upload,
        ping,
        jitter: Math.random() * 5 + 1, // Random jitter 1-6ms
        timestamp: new Date().toISOString()
      };
      
      setSpeedResult(result);
    } catch (err) {
      setError('Speed test failed. Please try again.');
    } finally {
      setIsTestingSpeed(false);
    }
  };

  const simulateSpeedTest = (type: 'download' | 'upload'): Promise<number> => {
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 20;
        if (progress >= 100) {
          clearInterval(interval);
          // Simulate realistic speeds (Mbps)
          const speed = type === 'download' 
            ? Math.random() * 80 + 20  // 20-100 Mbps
            : Math.random() * 40 + 10; // 10-50 Mbps
          resolve(Math.round(speed * 100) / 100);
        }
      }, 200);
    });
  };

  const simulatePingTest = (): Promise<number> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const ping = Math.random() * 50 + 10; // 10-60ms
        resolve(Math.round(ping * 100) / 100);
      }, 1000);
    });
  };

  const analyzeWebsite = async () => {
    if (!websiteUrl.trim()) return;
    
    setIsAnalyzingWebsite(true);
    setError(null);
    
    try {
      const url = websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`;
      const domain = new URL(url).hostname;
      
      const startTime = Date.now();
      
      // In a real app, you'd use your backend API to get this info
      // For demo, we'll simulate the response
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const responseTime = Date.now() - startTime;
      
      const mockInfo: WebsiteInfo = {
        domain,
        ip: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
        status: '200 OK',
        responseTime,
        headers: {
          'Content-Type': 'text/html; charset=utf-8',
          'Server': 'nginx/1.18.0',
          'X-Powered-By': 'Express',
          'Cache-Control': 'no-cache'
        }
      };
      
      setWebsiteInfo(mockInfo);
    } catch (err) {
      setError('Failed to analyze website. Please check the URL.');
    } finally {
      setIsAnalyzingWebsite(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatSpeed = (speed: number) => {
    if (speed >= 1000) {
      return `${(speed / 1000).toFixed(1)} Gbps`;
    }
    return `${speed} Mbps`;
  };

  const getSpeedColor = (speed: number, type: 'download' | 'upload') => {
    const threshold = type === 'download' ? 25 : 10;
    if (speed >= threshold * 2) return 'text-green-600';
    if (speed >= threshold) return 'text-yellow-600';
    return 'text-red-600';
  };

  const tabs = [
    { id: 'speed', label: 'Speed Test', icon: Zap },
    { id: 'ip', label: 'My IP Info', icon: Globe },
    { id: 'website', label: 'Website Analyzer', icon: Search },
    { id: 'dns', label: 'DNS Lookup', icon: Server }
  ];

  return (
    <div className="dev-utils-page" style={{ minHeight: "100vh", background: "var(--lyra-color-surface-2)", fontFamily: "'DM Sans', system-ui, sans-serif", padding: "28px 28px 48px" }}>
      <PageTracker pageTitle="Network Analyzer" category="dev-utils" />
      <div>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <Wifi size={20} style={{ color: "var(--app-primary-mid)" }} />
            <span style={{ fontSize: 22, fontWeight: 600, color: "var(--lyra-color-text)", fontFamily: "DM Sans, system-ui, sans-serif" }}>Network Analyzer</span>
          </div>
          <p style={{ fontSize: 13, color: "var(--app-text-2)", margin: 0 }}>Test your internet speed, analyze IP information, and perform network diagnostics</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-red-700 dark:text-red-400">{error}</span>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center gap-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon size={18} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Speed Test Tab */}
            {activeTab === 'speed' && (
              <div>
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Internet Speed Test
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Test your download and upload speeds
                  </p>
                </div>

                {!speedResult && !isTestingSpeed && (
                  <div className="text-center">
                    <button
                      onClick={runSpeedTest}
                      className="flex items-center gap-3 px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto text-lg font-medium"
                    >
                      <Zap size={24} />
                      Start Speed Test
                    </button>
                  </div>
                )}

                {isTestingSpeed && (
                  <div className="text-center">
                    <div className="mb-6">
                      <Loader className="h-16 w-16 text-blue-600 mx-auto animate-spin" />
                      <p className="text-lg font-medium text-gray-900 dark:text-white mt-4">
                        Testing your connection...
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        This may take up to 30 seconds
                      </p>
                    </div>
                  </div>
                )}

                {speedResult && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 text-center">
                      <Download className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Download</p>
                      <p className={`text-3xl font-bold ${getSpeedColor(speedResult.download, 'download')}`}>
                        {formatSpeed(speedResult.download)}
                      </p>
                    </div>

                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 text-center">
                      <Upload className="h-8 w-8 text-green-600 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Upload</p>
                      <p className={`text-3xl font-bold ${getSpeedColor(speedResult.upload, 'upload')}`}>
                        {formatSpeed(speedResult.upload)}
                      </p>
                    </div>

                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6 text-center">
                      <Timer className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Ping</p>
                      <p className="text-3xl font-bold text-purple-600">
                        {speedResult.ping} ms
                      </p>
                    </div>
                  </div>
                )}

                {speedResult && (
                  <div className="text-center">
                    <button
                      onClick={runSpeedTest}
                      disabled={isTestingSpeed}
                      className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors mx-auto"
                    >
                      <RefreshCw size={18} />
                      Test Again
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* IP Info Tab */}
            {activeTab === 'ip' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Your IP Information
                  </h2>
                  <button
                    onClick={loadMyIPInfo}
                    disabled={isLoadingIP}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <RefreshCw className={`h-4 w-4 ${isLoadingIP ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>

                {isLoadingIP ? (
                  <div className="text-center py-12">
                    <Loader className="h-8 w-8 text-blue-600 mx-auto animate-spin mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">Loading IP information...</p>
                  </div>
                ) : myIP ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Public IP Address</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{myIP.ip}</p>
                          </div>
                          <button
                            onClick={() => copyToClipboard(myIP.ip)}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="h-4 w-4 text-red-600" />
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Location</p>
                        </div>
                        <p className="text-gray-900 dark:text-white">{myIP.city}, {myIP.region}</p>
                        <p className="text-gray-900 dark:text-white">{myIP.country} ({myIP.countryCode})</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {myIP.lat.toFixed(4)}, {myIP.lon.toFixed(4)}
                        </p>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Router className="h-4 w-4 text-blue-600" />
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">ISP Information</p>
                        </div>
                        <p className="text-gray-900 dark:text-white">{myIP.isp}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{myIP.as}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Globe className="h-4 w-4 text-green-600" />
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Timezone</p>
                        </div>
                        <p className="text-gray-900 dark:text-white">{myIP.timezone}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date().toLocaleString()}
                        </p>
                      </div>

                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="h-4 w-4 text-blue-600" />
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Privacy Info</p>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Your IP address is visible to websites you visit. Consider using a VPN for enhanced privacy.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Website Analyzer Tab */}
            {activeTab === 'website' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Website Analyzer
                </h2>

                <div className="mb-6">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="Enter website URL (e.g., google.com)"
                      className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      onClick={analyzeWebsite}
                      disabled={isAnalyzingWebsite || !websiteUrl.trim()}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isAnalyzingWebsite ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search size={18} />
                      )}
                      Analyze
                    </button>
                  </div>
                </div>

                {websiteInfo && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Domain</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{websiteInfo.domain}</p>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">IP Address</p>
                        <div className="flex items-center justify-between">
                          <p className="text-gray-900 dark:text-white">{websiteInfo.ip}</p>
                          <button
                            onClick={() => copyToClipboard(websiteInfo.ip)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Response Time</p>
                        <p className="text-gray-900 dark:text-white">{websiteInfo.responseTime} ms</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Status</p>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <p className="text-gray-900 dark:text-white">{websiteInfo.status}</p>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">HTTP Headers</p>
                        <div className="space-y-2">
                          {Object.entries(websiteInfo.headers).map(([key, value]) => (
                            <div key={key} className="text-sm">
                              <span className="font-medium text-gray-900 dark:text-white">{key}:</span>
                              <span className="text-gray-600 dark:text-gray-400 ml-2">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* DNS Lookup Tab */}
            {activeTab === 'dns' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  DNS Lookup
                </h2>
                <div className="text-center py-12">
                  <Server className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">DNS lookup feature coming soon...</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                About Network Analyzer
              </h3>
              <div className="text-blue-800 dark:text-blue-200 text-sm space-y-1">
                <p>• <strong>Speed Test:</strong> Measures your internet download/upload speeds and latency</p>
                <p>• <strong>IP Info:</strong> Shows your public IP address, location, and ISP details</p>
                <p>• <strong>Website Analyzer:</strong> Analyzes website performance and server information</p>
                <p>• <strong>Privacy:</strong> All tests are performed client-side when possible for security</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
